import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MdsIcon } from '@mds/icons';
import { SKIP_ERROR_SNACKBAR } from '../interceptors/http-error.interceptor';
import { HostnameService } from './hostname.service';

export enum TaxonomyActionMenuKeyEnum {
  RenameTaxonomy = 'rename',
  AddNestedTaxonomy = 'add_new_nested',
  MoveTaxonomy = 'move',
  DeleteTaxonomy = 'delete',
  DeleteProduct = 'delete_product',
  RemoveContentFromTaxonomy = 'remove_products',
  GoToDefinition = 'go_to_definition',
  ViewModel = 'view_model',
  EpdExtrapolation = 'enable_epd_extrapolation'
}

export interface TaxonomyResultItem {
  rid: string;
  name: string;
  level: number;
  family_rid: string;
  products_included: string[];
  is_reference: boolean;
  reference_product: string;
  type: string;
  menu_actions: { [key: string]: boolean };
  tag?: {
    text: string;
    icon: MdsIcon;
    type: string;
  };
  parents: string[];
  children: TaxonomyResultItem[];
  prop_PN: string;
  has_products: boolean;
  menuOptions?: any[];
  actions: { [key: string]: boolean };
}

export interface TaxonomyMenuSection {
  description: string;
  field: string;
  formSchema: any[];
  icon: MdsIcon;
  key: TaxonomyActionMenuKeyEnum;
  lines: any[];
  name: string;
  options: any[];
  sortlevel: number;
}

export interface TaxonomyMenuGroup {
  key: string;
  name: string;
  sections: TaxonomyMenuSection[];
}

export interface TaxonomyMenus {
  productMenus: TaxonomyMenuGroup[];
  taxonomyMenus: TaxonomyMenuGroup[];
}

export interface TaxonomiesAndContent {
  data: TaxonomyResultItem[];
  formSchema: {
    menus: TaxonomyMenus;
  };
}

export interface AddNestedTaxonomyPayload {
  name: string;
  code: string;
  level: number;
  in_child: [{ out: string }];
}

export interface AddNestedTaxonomyResponse {
  rid: string;
}

export interface UpdateTaxonomyNamePayload {
  name: string;
  code: string;
}

export interface UpdateTaxonomyNameResponse {
  rid: string;
  updated: {
    code: string;
    edges_updated: any[];
    name: string;
    updated_on: string;
  };
}

export interface MoveTaxonomyPayload {
  level: number;
  in_child: [{ out: string }];
}

// TODO: is this correct response?
export interface MoveTaxonomyResponse {
  rid: string;
}

export interface AddProductGroupToTaxonomyPayload {
  taxonomy_rid: string;
  products_included: string[];
}

export interface AddProductGroupToTaxonomyResponse {
  rid: string;
  updated: {
    edges_updated: string[];
    products_included: string[];
    taxonomy_rid: string;
    updated_on: string;
  };
}

export interface SetProductGroupAsReferencePayload {
  taxonomy_rid: string;
  reference_product: string;
}

export interface SetProductGroupAsReferenceResponse {
  rid: string;
}

export interface DeleteProductGroupFromTaxonomyPayload {
  product_group_rids: string[];
}

@Injectable({ providedIn: 'root' })
export class TaxonomyService {
  baseUrl: string;

  constructor(
    private httpClient: HttpClient,
    private hostnameService: HostnameService
  ) {
    this.baseUrl = this.hostnameService.getOrigin();
  }

  getTaxonomiesAndContent() {
    return this.httpClient.get<TaxonomiesAndContent>(`${this.baseUrl}/api/v1/taxonomies`);
  }

  updateTaxonomyName(rid: string, payload: UpdateTaxonomyNamePayload) {
    return this.httpClient.put<UpdateTaxonomyNameResponse>(
      `${this.baseUrl}/api/v1/composer/route/${rid.replace('#', '')}`,
      payload
    );
  }

  addNestedTaxonomy(payload: AddNestedTaxonomyPayload) {
    return this.httpClient.post<AddNestedTaxonomyResponse>(`${this.baseUrl}/api/v1/composer/route/taxonomy`, payload);
  }

  moveTaxonomy(rid: string, payload: MoveTaxonomyPayload) {
    return this.httpClient.put<MoveTaxonomyResponse>(
      `${this.baseUrl}/api/v1/composer/route/${rid.replace('#', '')}`,
      payload
    );
  }

  addProductGroupToTaxonomy(familyRid: string, payload: AddProductGroupToTaxonomyPayload) {
    if (familyRid) {
      return this.httpClient.put<AddProductGroupToTaxonomyResponse>(
        `${this.baseUrl}/api/v1/composer/route/${familyRid.replace('#', '')}`,
        payload,
        { context: new HttpContext().set(SKIP_ERROR_SNACKBAR, true) }
      );
    }

    return this.httpClient.post<AddProductGroupToTaxonomyResponse>(
      `${this.baseUrl}/api/v1/composer/route/taxonomy_family`,
      payload
    );
  }

  setProductGroupAsReference(payload: SetProductGroupAsReferencePayload) {
    return this.httpClient.post<SetProductGroupAsReferenceResponse>(
      `${this.baseUrl}/api/v1/composer/route/taxonomy_family`,
      payload
    );
  }

  updateProductGroupReference(familyRid: string, payload: SetProductGroupAsReferencePayload) {
    return this.httpClient.put<SetProductGroupAsReferenceResponse>(
      `${this.baseUrl}/api/v1/composer/route/${familyRid.replace('#', '')}`,
      payload
    );
  }

  deleteProductGroupsFromTaxonomy(taxonomyRid: string, payload: DeleteProductGroupFromTaxonomyPayload) {
    return this.httpClient.delete<null>(
      `${this.baseUrl}/api/v1/taxonomies/${taxonomyRid.replace('#', '')}/products-included`,
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        body: payload
      }
    );
  }

  removeAllContentFromTaxonomy(familyRid: string) {
    return this.httpClient.delete<null>(`${this.baseUrl}/api/v1//composer/route/${familyRid.replace('#', '')}`);
  }

  uploadTaxonomies(fileObj: FormData) {
    return this.httpClient.post<undefined>(`${this.baseUrl}/api/v1/import/taxonomy_importer`, fileObj);
  }

  uploadBulkAssigment(fileObj: FormData) {
    return this.httpClient.post<undefined>(`${this.baseUrl}/api/v1/import/taxonomy_assignments_importer`, fileObj);
  }

  downloadTaxonomies() {
    return this.httpClient.get(`${this.baseUrl}/api/v1/taxonomies/export`, {
      responseType: 'blob',
      observe: 'response'
    });
  }

  fetchChildren(familyRid: string) {
    return this.httpClient.get<TaxonomiesAndContent>(
      `${this.baseUrl}/api/v1/taxonomies/${familyRid.replace('#', '')}/expand`
    );
  }

  fetchResultsByPartNumber(searchTerm: string) {
    return this.httpClient.get<TaxonomiesAndContent>(
      `${this.baseUrl}/api/v1/taxonomies/search?partNumber=${searchTerm}`
    );
  }
}
